import { useOrchestrationFabrics } from '@common/Fabric/hooks';
import { useOrchestrationMultiFabricQuery } from '@common/Fabric/OrchestrationFabric/api';
import { useOrchestrationHistoryApis } from '@common/Fabric/OrchestrationFabric/useOrchestrationHistoryApis';
import { useUserSQLProjects } from '@common/projectHooks/useUserSQLProjects';
import { Entity, getAppEntityName } from '@common/types/Entity';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useIsVerticalApp } from '@common/VerticalApps/utils';
import {
  ArrowNarrowUpRightIcon,
  CheckCircleIcon,
  InfoCircleIcon,
  PlayCircleIcon,
  PlayIcon,
  StopCircleIcon,
  TrashCIcon
} from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { Dialog } from '@prophecy/ui-v3/Dialog';
import { StyledLabel } from '@prophecy/ui-v3/Input';
import { Stack } from '@prophecy/ui-v3/Layout';
import { RadialIconContainer } from '@prophecy/ui-v3/Misc/RadialIconContainer';
import { DateRangeInput } from '@prophecy/ui-v3/Picker/inputVariants/DateRangeInput';
import { DateRange, PICKER_VARIANT } from '@prophecy/ui-v3/Picker/types';
import { Select } from '@prophecy/ui-v3/Select';
import { Table, TABLE_ROW_CLASS } from '@prophecy/ui-v3/Table';
import { TABLE_BODY } from '@prophecy/ui-v3/Table/tokens';
import { Text } from '@prophecy/ui-v3/Typography';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { formattedDateTime, getDuration } from '@prophecy/utils/date';
import { getHttpStatus } from '@prophecy/utils/error';
import { useSearchParamState } from '@prophecy/utils/react/hooks';
import { parseJSON } from '@prophecy/utils/string';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { OrchestrationPipelineStatus, SqlPipelineRunType } from 'frontend/core/src/data/apis/types';
import { SlidingDetailsDialog } from 'frontend/core/src/generic-components/Logger/SlidingDetailsDialog';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { castArray } from '../../../data/util';
import { isReplayMode } from '../../../utils/replayToken';
import { isShareMode } from '../../../utils/shareToken';
import TriggerBagdes from '../Components/TriggerBagdes';
import { SQLPipelineObservationLayout } from './Layout';
import { RestartBadges } from './RestartBadges';
import { isAttributionInfoAvailable } from './RunAttribution/isAttributionInfoAvailable';
import { RunDetailsDrawer } from './RunAttribution/RunDetailsDrawer';
import { useRunPersonResolver } from './RunAttribution/useRunPersonResolver';
import { StyledSelect } from './styled';
import { PipelineRunRecord, SQLPipelineRunStatus } from './types';
import {
  getDisplayRunType,
  getRunLogsUrl,
  getRunStatusBadge,
  isRunInFlight,
  withEllipsis,
  renderPipelineLink,
  renderProjectLink
} from './utils';

// buttons only sit next to Running (Stop) and Failed / Cancelled (Restart), so the slot fits those badges and the buttons line up
const StatusBadgeSlot = styled(Stack)`
  min-width: 94px;
`;

const RUNS_TABLE_TEST_ID = 'sql-pipeline-runs-table';
const ROW_FLASH_MS = 1000;
// a virtualized target row only mounts once the scroll lands, so look for it over a few frames
const ROW_FLASH_MAX_FRAMES = 30;

// Animates the rendered cells directly: rc-table caches its rows, so a className set through onRow never
// reaches the DOM. Only the midpoint is given, so the row fades from and back to its own background.
function flashRunRow(runId: string, framesLeft = ROW_FLASH_MAX_FRAMES) {
  const row = document.querySelector(`[data-test-id="${RUNS_TABLE_TEST_ID}"] tr[data-row-key="${CSS.escape(runId)}"]`);
  if (!row) {
    if (framesLeft > 0) requestAnimationFrame(() => flashRunRow(runId, framesLeft - 1));
    return;
  }
  row.querySelectorAll('td').forEach((cell) =>
    cell.animate([{ backgroundColor: theme.colors.gray200, offset: 0.5 }], {
      duration: ROW_FLASH_MS,
      easing: 'ease-in-out'
    })
  );
}

// Table exposes no ref, so reach its scroll body the way its own column filters do
function scrollRunsTableToTop() {
  document
    .querySelector(`[data-test-id="${RUNS_TABLE_TEST_ID}"] .${TABLE_BODY}`)
    ?.scrollTo({ top: 0, behavior: 'smooth' });
}

// fetchURL throws the parsed JSON error body, or a ProphecyError carrying a plain-text body as its message
function getRequestErrorMessage(err: unknown) {
  const { error, message } = (err ?? {}) as { error?: string | { message?: string }; message?: string };
  return (typeof error === 'string' ? error : error?.message) || message || 'Something went wrong';
}

const SELECTED_ROW_CLASS = 'sql-pipeline-runs-selected-row';
const DETAILS_BUTTON_CLASS = 'sql-pipeline-runs-details-button';

const StyledRunsTable = styled(Table)`
  .${TABLE_ROW_CLASS}:hover > td,
  .${SELECTED_ROW_CLASS} > td {
    background: ${theme.colors.gray50};
  }
  .${DETAILS_BUTTON_CLASS} {
    opacity: 0;
  }
  .${TABLE_ROW_CLASS}:hover
    .${DETAILS_BUTTON_CLASS},
    .${SELECTED_ROW_CLASS}
    .${DETAILS_BUTTON_CLASS},
    .${DETAILS_BUTTON_CLASS}:focus-visible {
    opacity: 1;
  }
` as unknown as typeof Table;

// orphaned RUNNING/CANCELLING runs are also restartable, but the UI cannot tell them from live ones
const RESTARTABLE_STATUSES = new Set<string>([
  SQLPipelineRunStatus.ERROR,
  SQLPipelineRunStatus.WARNING,
  SQLPipelineRunStatus.CANCELLED
]);

function openRunAlreadyCompletedDialog() {
  return Dialog.open({
    showCloseIcon: true,
    size: 'm',
    closeButton: null,
    okButton: { children: 'Close' },
    'data-test-id': 'sql-pipeline-runs-already-completed-dialog',
    style: {
      width: '496px'
    },
    icon: (
      <RadialIconContainer tone={BaseColor.success}>
        <CheckCircleIcon type='default' />
      </RadialIconContainer>
    ),
    children: (
      <Stack gap={theme.spaces.x4} padding={`0 ${theme.spaces.x16} 0 ${theme.spaces.x16}`}>
        <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
          Run already completed
        </Text>
        <Text level='sm' tone={theme.colors.gray600}>
          This run finished successfully before it could be stopped. There is nothing to cancel.
        </Text>
      </Stack>
    )
  });
}

const StyledRunTypeSelect = styled(Select)`
  min-width: 300px;
`;

const Link = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.x4};
` as unknown as typeof Button;
function useFetchRuns({
  fabricId,
  projectId,
  minTime,
  maxTime,
  runTypes
}: {
  fabricId?: string | string[];
  projectId?: string | string[];
  pipelineName?: string;
  minTime?: Date;
  maxTime?: Date;
  runTypes?: SqlPipelineRunType[];
}) {
  const { fabrics, isLoading: isJWTLoading, fabricsMap } = useOrchestrationFabrics();

  const { getPipelineRuns } = useOrchestrationHistoryApis();
  const {
    data,
    isInitialLoading: isLoading,
    invalidate
  } = useOrchestrationMultiFabricQuery(
    ['orchestration-pipeline-runs', fabricId, projectId, minTime, maxTime, runTypes],
    () =>
      getPipelineRuns({
        projectIds: projectId ? castArray(projectId) : undefined,
        fabricIds: fabricId ? castArray(fabricId) : undefined,
        minTime,
        maxTime,
        runTypes
      }),
    {
      refetchInterval: 5000
    }
  );

  const runs = useMemo((): PipelineRunRecord[] => {
    return castArray(data?.data.history).map((run) => ({
      ...run,
      sensorConfigDetails: parseJSON(run.sensorConfigDetails, [])
    }));
  }, [data]);

  return {
    runs,
    isLoading: isLoading || isJWTLoading,
    fabrics,
    fabricsMap,
    invalidate
  };
}

const All_RUN_TYPES = [
  SqlPipelineRunType.Scheduled,
  SqlPipelineRunType.InteractivePipelineRun,
  SqlPipelineRunType.InteractiveAppRun,
  SqlPipelineRunType.AppSchedule,
  SqlPipelineRunType.APIPipelineRun
];

// TODO: @ameerthehacker add pagination
export function PipelineRuns() {
  const [fabricId, setFabricId] = useSearchParamState('prFabricId');
  const [projectId, setProjectId] = useSearchParamState('prProjectId');
  const { projectsMap, isLoading: isProjectsLoading, projects } = useUserSQLProjects();
  const [messageToShow, setMessageToShow] = useState<{
    message: string;
    type: 'error' | 'warning' | 'cancelled';
  } | null>(null);
  const [dateRange, setDateRange] = useSearchParamState<[Date | undefined, Date | undefined]>('prDateRange', {
    initialValue: [undefined, undefined],
    serialize: (value) => value.map((date) => date?.toISOString()).join(','),
    deserialize: (value) =>
      value.split(',').map((date) => (date ? new Date(date) : undefined)) as [Date | undefined, Date | undefined]
  });
  const [runTypes, setRunTypes] = useSearchParamState('prRunTypes', {
    initialValue: All_RUN_TYPES,
    serialize: (value) => value.join(','),
    deserialize: (value) => (value ? value.split(',') : FROZEN_EMPTY_ARRAY) as SqlPipelineRunType[]
  });

  const {
    isLoading: isRunsLoading,
    runs: fetchedRuns,
    fabrics,
    fabricsMap,
    invalidate
  } = useFetchRuns({
    fabricId,
    projectId: projectId || projects.map((project) => project._id),
    minTime: dateRange?.[0] ? new Date(dateRange[0].setHours(0, 0, 0, 0)) : undefined,
    maxTime: dateRange?.[1] ? new Date(dateRange[1].setHours(23, 59, 59)) : undefined,
    runTypes: !runTypes || runTypes.length === 0 ? All_RUN_TYPES : runTypes
  });

  const { isHistoricalRunSnapshotsEnabled } = useGlobalVariables();
  const isVerticalApp = useIsVerticalApp();
  const { cancelPipelineRun, restartPipelineRun } = useOrchestrationHistoryApis();

  // runIds the backend has confirmed as cancelling - overlaid so the row flips before the next poll lands
  const [pendingCancelRunIds, setPendingCancelRunIds] = useState<Set<string>>(new Set());
  // runIds with a cancel request in flight - guards against a double click firing two calls
  const [cancellingRequestRunIds, setCancellingRequestRunIds] = useState<Set<string>>(new Set());

  const runs = useMemo(
    () =>
      fetchedRuns.map((run) =>
        pendingCancelRunIds.has(run.runId) && run.runStatus === SQLPipelineRunStatus.RUNNING
          ? { ...run, runStatus: SQLPipelineRunStatus.CANCELLING }
          : run
      ),
    [fetchedRuns, pendingCancelRunIds]
  );

  const canCancelRuns = !isShareMode() && !isReplayMode();

  const resolvePerson = useRunPersonResolver();
  const runsById = useMemo(() => new Map(runs.map((run) => [run.runId, run])), [runs]);
  // runs opened in the drawer, most recent last - following a parent/re-run link pushes, Back pops
  const [openedRunIds, setOpenedRunIds] = useState<string[]>([]);
  const selectedRunId = openedRunIds.at(-1);
  // looked up on every poll so the drawer follows the run's live status
  const selectedRun = selectedRunId ? runsById.get(selectedRunId) : undefined;

  const showRunMessage = (record: PipelineRunRecord) =>
    setMessageToShow({
      message: record.errorMessage || '',
      type:
        record.runStatus === SQLPipelineRunStatus.WARNING
          ? 'warning'
          : record.runStatus === SQLPipelineRunStatus.CANCELLED
            ? 'cancelled'
            : 'error'
    });

  // runIds with a restart request in flight - guards against a double click firing two calls
  const [restartingRequestRunIds, setRestartingRequestRunIds] = useState<Set<string>>(new Set());
  const restartedRunIds = useMemo(() => new Set(runs.map((run) => run.restartOfRunId).filter(Boolean)), [runs]);
  const rowIndexByRunId = useMemo(() => new Map(runs.map((run, index) => [run.runId, index])), [runs]);

  // only the latest attempt in a restart chain can be restarted
  const isRestartable = (record: (typeof runs)[number]) =>
    canCancelRuns &&
    record.runType === SqlPipelineRunType.Scheduled &&
    // without a cron or sensor trigger the Run Type badge falls back to a bare "Scheduled Run" - not restartable
    (Boolean(record.cron) || record.sensorConfigDetails.length > 0) &&
    RESTARTABLE_STATUSES.has(record.runStatus) &&
    !restartedRunIds.has(record.runId);

  const handleRestartRun = async (record: (typeof runs)[number]) => {
    if (restartingRequestRunIds.has(record.runId)) return;
    const confirmed = await Dialog.open({
      showCloseIcon: true,
      size: 'm',
      title: 'Restart this run?',
      icon: (
        <RadialIconContainer tone={BaseColor.primary}>
          <PlayIcon type='default' />
        </RadialIconContainer>
      ),
      iconPlacement: 'left',
      style: {
        width: '496px'
      },
      children: (
        <Stack gap={theme.spaces.x16} padding={`${theme.spaces.x16} 0 ${theme.spaces.x16} 0`}>
          <Text level='sm' tone={theme.colors.gray600}>
            This starts a new attempt of {record.pipelineName} with the schedule&apos;s current version and parameters.
          </Text>
        </Stack>
      ),
      closeButton: { children: 'Cancel' },
      okButton: { children: 'Restart run', variant: 'primary' },
      'data-test-id': 'sql-pipeline-runs-restart-confirm-dialog'
    });
    if (confirmed !== Dialog.ResponseAction.okButton) return;

    setRestartingRequestRunIds((prev) => new Set(prev).add(record.runId));
    try {
      const { data } = await restartPipelineRun({ runId: record.runId, latestRunId: record.runId });
      toast.success({ content: `Restarted as attempt ${data.attempt}` });
      scrollRunsTableToTop();
      invalidate();
    } catch (err) {
      const errorMessage = getRequestErrorMessage(err);
      toast.error({ content: errorMessage, 'data-test-id': 'sql-pipeline-runs-restart-error-toast' });
      // the run is gone or no longer restartable (e.g. already restarted elsewhere) - refresh to show why
      const status = getHttpStatus(err);
      if (status === 404 || status === 409) invalidate();
    } finally {
      setRestartingRequestRunIds((prev) => {
        const next = new Set(prev);
        next.delete(record.runId);
        return next;
      });
    }
  };

  // runId whose confirm dialog is open - lets the effect below close it if the run finishes on its own
  const [confirmingRunId, setConfirmingRunId] = useState<string | null>(null);
  const closeConfirmDialogRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!confirmingRunId) return;
    const run = runs.find((run) => run.runId === confirmingRunId);
    if (!run || run.runStatus === SQLPipelineRunStatus.RUNNING) return;

    const closeDialog = closeConfirmDialogRef.current;
    if (!closeDialog) return;
    closeConfirmDialogRef.current = null;
    closeDialog();
    if (run.runStatus === SQLPipelineRunStatus.SUCCEEDED) {
      void openRunAlreadyCompletedDialog();
    }
  }, [runs, confirmingRunId]);

  const handleStopRun = async (record: (typeof runs)[number]) => {
    if (cancellingRequestRunIds.has(record.runId)) return;
    setConfirmingRunId(record.runId);
    const confirmed = await Dialog.open({
      showCloseIcon: true,
      size: 'm',
      title: 'Stop this run?',
      icon: (
        <RadialIconContainer tone={BaseColor.error}>
          <TrashCIcon type='default' />
        </RadialIconContainer>
      ),
      iconPlacement: 'left',
      style: {
        width: '496px'
      },
      children: ({ onClose }) => {
        closeConfirmDialogRef.current = () => onClose();
        return (
          <Stack gap={theme.spaces.x16} padding={`${theme.spaces.x16} 0 ${theme.spaces.x16} 0`}>
            <Text level='sm' tone={theme.colors.gray600}>
              This stops the run for {record.pipelineName}. Any work in progress is discarded and the run is marked as
              Cancelled.
            </Text>
          </Stack>
        );
      },
      closeButton: { children: 'Cancel' },
      okButton: { children: 'Stop run', variant: 'primary', danger: true },
      'data-test-id': 'sql-pipeline-runs-stop-confirm-dialog'
    });
    closeConfirmDialogRef.current = null;
    setConfirmingRunId(null);
    if (confirmed !== Dialog.ResponseAction.okButton) return;

    setCancellingRequestRunIds((prev) => new Set(prev).add(record.runId));
    try {
      const { data } = await cancelPipelineRun({ runId: record.runId });
      if (data.status === OrchestrationPipelineStatus.Cancelling) {
        setPendingCancelRunIds((prev) => new Set(prev).add(record.runId));
      } else if (data.status === OrchestrationPipelineStatus.AlreadyFinished) {
        if (data.runStatus === SQLPipelineRunStatus.SUCCEEDED) {
          void openRunAlreadyCompletedDialog();
        } else {
          toast.info({ content: data.message || 'Run already finished' });
        }
      } else if (data.status === OrchestrationPipelineStatus.Cancelled) {
        toast.success({ content: data.message || 'Run cancelled' });
      }
      invalidate();
    } catch (err) {
      const errorMessage = getRequestErrorMessage(err);
      toast.error({ content: errorMessage, 'data-test-id': 'sql-pipeline-runs-stop-error-toast' });
      if (getHttpStatus(err) === 404) invalidate();
    } finally {
      setCancellingRequestRunIds((prev) => {
        const next = new Set(prev);
        next.delete(record.runId);
        return next;
      });
    }
  };

  return (
    <SQLPipelineObservationLayout title='Run History' count={runs.length}>
      <Stack direction='horizontal' gap={theme.spaces.x20}>
        <Stack gap={theme.spaces.x6}>
          <StyledLabel level='md'>Date Range</StyledLabel>
          <DateRangeInput
            dateVariant={PICKER_VARIANT.DATE_RANGE}
            inputContainerSize='m'
            inputSize='s'
            showActionFooter
            pickerProps={{
              showPresets: true,
              allowClear: true
            }}
            value={dateRange}
            onChange={setDateRange as (value: DateRange | [string, string]) => unknown}
            data-test-id='sql-pipeline-runs-date-range-picker'
          />
        </Stack>
        {!isVerticalApp && (
          <StyledSelect
            label='Fabric'
            allowClear
            placeholder='Select Fabric'
            data-test-id='sql-pipeline-runs-fabric-select'
            options={fabrics.map((fabric) => ({
              label: fabric.name,
              value: fabric.id
            }))}
            value={fabricId}
            onChange={setFabricId}
          />
        )}
        <StyledSelect
          label='Project'
          allowClear
          placeholder='Select Project'
          options={projects.map((project) => ({
            label: project.name,
            value: project._id
          }))}
          value={projectId}
          onChange={setProjectId}
          data-test-id='sql-pipeline-runs-project-select'
        />
        <StyledRunTypeSelect
          allowClear
          label='Run Type'
          mode='multiple'
          value={runTypes}
          onChange={setRunTypes}
          options={[
            {
              label: 'Scheduled',
              value: SqlPipelineRunType.Scheduled
            },
            {
              label: 'Pipeline Run',
              value: SqlPipelineRunType.InteractivePipelineRun
            },
            {
              label: `Scheduled ${getAppEntityName(Entity.App)} Run`,
              value: SqlPipelineRunType.AppSchedule
            },
            {
              label: `${getAppEntityName(Entity.App)} Run`,
              value: SqlPipelineRunType.InteractiveAppRun
            },
            {
              label: 'API Pipeline Run',
              value: SqlPipelineRunType.APIPipelineRun
            }
          ]}
          data-test-id='sql-pipeline-run-type-select'
        />
      </Stack>

      <StyledRunsTable
        loading={isRunsLoading || isProjectsLoading}
        virtualize
        height='100%'
        placeholderRows={50}
        width='100%'
        fixedHeader
        rowKey='runId'
        data-test-id={RUNS_TABLE_TEST_ID}
        onRow={(record) => ({
          className: record.runId === selectedRunId ? SELECTED_ROW_CLASS : undefined
        })}
        columns={[
          {
            key: 'runId',
            title: 'ID',
            width: '120px',
            render: (_, record) => withEllipsis(record.runId || '--')
          },
          {
            title: 'Fabric',
            render: (_, record) => {
              const fabric = fabricsMap.get(record.fabricId);
              return withEllipsis(fabric?.name || record.fabricId);
            },
            key: 'fabric'
          },
          {
            title: 'Pipeline',
            dataIndex: 'pipelineName',
            key: 'pipelineName',
            render: (_, record) =>
              renderPipelineLink({
                projectId: record.projectId,
                pipelineName: record.pipelineName,
                projectsMap,
                dataTestId: `sql-pipeline-runs-pipeline-${record.pipelineName}-link`
              })
          },
          {
            title: 'Project',
            render: (_, record) =>
              renderProjectLink({
                projectId: record.projectId,
                projectsMap,
                dataTestId: `sql-pipeline-runs-project-${record.projectId}-link`
              }),
            key: 'projectId'
          },
          {
            title: 'Run Type',
            width: '20%',
            render: (_, record) => {
              return (
                <TriggerBagdes
                  cron={record.cron}
                  sensorConfigDetails={record.sensorConfigDetails}
                  runType={getDisplayRunType(record)}
                  appName={record.appName}
                  instanceName={record.instanceName}>
                  <RestartBadges
                    restartOfRunId={record.restartOfRunId}
                    rootRunId={record.rootRunId}
                    rowIndexByRunId={rowIndexByRunId}
                    onGoToRun={flashRunRow}
                  />
                </TriggerBagdes>
              );
            },
            key: 'runType'
          },
          {
            title: 'Start Time',
            render: (_, record) => {
              if (record.runStatus === SQLPipelineRunStatus.QUEUED) return '-';
              return renderColumnWithEllipsis(
                formattedDateTime(record.startTime, {
                  month: 'short',
                  day: 'numeric',
                  year: new Date().getFullYear() === new Date(record.startTime).getFullYear() ? undefined : 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric'
                })
              );
            },
            key: 'startTime'
          },
          {
            title: 'End Time',
            width: '120px',
            render: (_, record) => {
              if (record.runStatus === SQLPipelineRunStatus.QUEUED) return '-';
              if (!record.endTime || isRunInFlight(record.runStatus)) return 'N/A';
              const startDate = new Date(record.startTime);
              const endDate = new Date(record.endTime);
              const shouldShowDayInfo =
                startDate.getDate() === endDate.getDate() &&
                startDate.getMonth() === endDate.getMonth() &&
                startDate.getFullYear() === endDate.getFullYear() &&
                startDate.getFullYear() === new Date().getFullYear();

              return renderColumnWithEllipsis(
                formattedDateTime(record.endTime, {
                  month: shouldShowDayInfo ? undefined : 'short',
                  day: shouldShowDayInfo ? undefined : 'numeric',
                  year: shouldShowDayInfo ? undefined : 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric'
                })
              );
            },
            key: 'endTime'
          },
          {
            title: 'Duration',
            width: '100px',
            render: (_, record) => {
              if (record.runStatus === SQLPipelineRunStatus.QUEUED) return '-';
              const seconds = Math.round(record.duration_ns / 1000000000);

              return renderColumnWithEllipsis(isRunInFlight(record.runStatus) ? 'N/A' : getDuration(seconds, true));
            },
            key: 'duration'
          },
          ...(isHistoricalRunSnapshotsEnabled
            ? [
                {
                  title: 'Logs',
                  render: (_: unknown, record: PipelineRunRecord) => {
                    const logsUrl = getRunLogsUrl(record);
                    if (!logsUrl) {
                      return renderColumnWithEllipsis('No logs available');
                    }
                    return (
                      <Link
                        variant='linkGray'
                        elementType='link'
                        tooltip='Click to open pipeline in Historical mode and see all past runs'
                        data-test-id='sql-pipeline-runs-logs-link'
                        aria-label='View Logs'
                        to={logsUrl}
                        icon={<ArrowNarrowUpRightIcon type='default' size='xs' />}
                        iconPlacement='right'
                        size='xs'>
                        See logs
                      </Link>
                    );
                  },
                  width: '100px',
                  key: 'logs'
                }
              ]
            : []),
          {
            title: 'Status',
            width: '160px',
            render: (_, record) => {
              const showStop = canCancelRuns && record.runStatus === SQLPipelineRunStatus.RUNNING;
              return (
                <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
                  <StatusBadgeSlot direction='horizontal' alignY='center'>
                    {getRunStatusBadge(record.runStatus, record.errorMessage, () => showRunMessage(record))}
                  </StatusBadgeSlot>
                  {showStop && (
                    <Button
                      size='xs'
                      variant='linkGray'
                      danger
                      icon={<StopCircleIcon type='solid' size='xs' />}
                      tooltip='Stop run'
                      aria-label='Stop run'
                      disabled={cancellingRequestRunIds.has(record.runId)}
                      onClick={() => handleStopRun(record)}
                      data-test-id={`sql-pipeline-runs-stop-button-${record.runId}`}
                    />
                  )}
                  {isRestartable(record) && (
                    <Button
                      size='xs'
                      variant='linkGray'
                      icon={<PlayCircleIcon type='solid' size='xs' color={theme.colors.success600} />}
                      tooltip='Restart run'
                      aria-label='Restart run'
                      disabled={restartingRequestRunIds.has(record.runId)}
                      onClick={() => handleRestartRun(record)}
                      data-test-id={`sql-pipeline-runs-restart-button-${record.runId}`}
                    />
                  )}
                </Stack>
              );
            },
            key: 'status'
          },
          {
            title: '',
            width: '48px',
            render: (_, record) =>
              isAttributionInfoAvailable(record) && (
                <Button
                  className={DETAILS_BUTTON_CLASS}
                  size='xs'
                  variant='linkGray'
                  icon={<InfoCircleIcon type='default' size='xs' />}
                  tooltip='Run details'
                  aria-label='Run details'
                  onClick={() => setOpenedRunIds([record.runId])}
                  data-test-id={`sql-pipeline-runs-details-button-${record.runId}`}
                />
              ),
            key: 'details'
          }
        ]}
        dataSource={runs}
      />
      {selectedRun && (
        <RunDetailsDrawer
          run={selectedRun}
          teamId={fabricsMap.get(selectedRun.fabricId)?.team?._id}
          resolvePerson={resolvePerson}
          hasRun={(runId) => {
            const linkedRun = runsById.get(runId);
            return Boolean(linkedRun && isAttributionInfoAvailable(linkedRun));
          }}
          onSelectRun={(runId) => setOpenedRunIds((prev) => [...prev, runId])}
          onBack={openedRunIds.length > 1 ? () => setOpenedRunIds((prev) => prev.slice(0, -1)) : undefined}
          onShowMessage={() => showRunMessage(selectedRun)}
          onClose={() => setOpenedRunIds([])}
        />
      )}
      {messageToShow && (
        <SlidingDetailsDialog
          title={
            messageToShow.type === 'warning'
              ? 'Warning Details'
              : messageToShow.type === 'cancelled'
                ? 'Cancellation Details'
                : 'Error Details'
          }
          onClose={() => setMessageToShow(null)}
          message={messageToShow.message}
          type={messageToShow.type === 'cancelled' ? 'info' : messageToShow.type}
        />
      )}
    </SQLPipelineObservationLayout>
  );
}
